/** Данные графика тренда: расходы стеком (группы удержаний + себестоимость) + выручка, одна ось
 *  (₽); маржа (`net_margin`) — отдельным мини-графиком. Схема «Выручка − Расходы = Маржа» (решение
 *  владельца 22.10.2026): корзина без себестоимости (раньше `cogs_from`) — без столбца
 *  себестоимости, «Расходов» и «Маржи» (null — «—» в подсказке, разрыв на графике маржи), а не
 *  маржа до себестоимости.
 *
 *  Серии — ФИКСИРОВАННЫЙ порядок групп (цвет следует за группой, а не за рангом); групп
 *  удержаний 12, различимых цветов — 6 + себестоимость (слот 7 палитры dataviz, проверена
 *  валидатором), остальное — «Прочие удержания» серым. Неизвестный код группы (витрина разошлась с контрактом) тоже
 *  попадает в «прочие», но его код возвращается в `unknown` — страница показывает его явно.
 *  Справочные группы (`reference` в /meta, WB `spp`) в P&L не входят: не рисуются вовсе — ни
 *  своим рядом, ни в «прочих», и в `unknown` не попадают. */
import type { Grain, TrendBucket } from "@/api/ozonAnalytics";
import { cogsTotal } from "./cogs";
import { bucketLabel } from "./format";

export interface TrendSeries {
  key: string;
  label: string;
  light: string;
  dark: string;
}

export const TREND_SERIES: TrendSeries[] = [
  { key: "comm", label: "Комиссия", light: "#2a78d6", dark: "#3987e5" },
  { key: "log", label: "Логистика и доставка", light: "#eb6834", dark: "#d95926" },
  { key: "ret", label: "Возвраты и невыкупы", light: "#1baf7a", dark: "#199e70" },
  { key: "supply", label: "Кросс-докинг и ошибки поставок", light: "#eda100", dark: "#c98500" },
  { key: "ads", label: "Реклама и продвижение", light: "#e87ba4", dark: "#d55181" },
  { key: "stor", label: "Хранение", light: "#008300", dark: "#008300" },
  { key: "cogs", label: "Себестоимость", light: "#6250d6", dark: "#9085e9" },
  { key: "rest", label: "Прочие удержания", light: "#a8a29e", dark: "#78716c" },
];

/** Серии графика с площадкой в подписи комиссии («Комиссия Ozon», «Комиссия WB»). */
export function trendSeries(mpLabel: string): TrendSeries[] {
  return TREND_SERIES.map((s) => (s.key === "comm" ? { ...s, label: `Комиссия ${mpLabel}` } : s));
}

/** Группы витрины, которые идут одним рядом: кросс-докинг и ошибки поставок — то, что разбирают
 *  по поставкам, поэтому отдельным цветом, а не в «Прочих». */
const GROUP_TO_SERIES: Record<string, string> = { xd: "supply", sup: "supply" };
/** Группы, которые сворачиваются в «Прочие удержания». */
export const REST_GROUPS = ["acq", "rem", "ins", "subs", "other"];
/** Ряд себестоимости — не группа витрины в `by_grp`, а `cogs_goods + cogs_wh` корзины. */
export const COGS_SERIES = "cogs";
const NAMED = new Set(
  TREND_SERIES.map((s) => s.key).filter((k) => k !== "rest" && k !== COGS_SERIES),
);
const KNOWN = new Set([...NAMED, ...Object.keys(GROUP_TO_SERIES), ...REST_GROUPS, "rev"]);

export type TrendPoint = {
  bucket: string;
  label: string;
  rev: number;
  /** «Расходы» корзины (удержания + себестоимость, в знаке витрины); null — без себестоимости */
  expenses: number | null;
  /** «Маржа» (`net_margin`); null — без себестоимости (разрыв, не маржа до себестоимости) */
  margin: number | null;
  /** себестоимость корзины посчитана (иначе ряд «Себестоимость» — 0 в стеке и «—» в подсказке) */
  hasCogs: boolean;
} & Record<string, number | string | boolean | null>;

export function trendPoints(
  buckets: TrendBucket[],
  grain: Grain,
  /** коды справочных групп площадки (из /meta) */
  reference: ReadonlySet<string> = new Set(),
): { points: TrendPoint[]; unknown: string[] } {
  const unknown = new Set<string>();
  const points = buckets.map((b) => {
    const cogs = cogsTotal(b.cogs_goods, b.cogs_wh);
    const p: TrendPoint = {
      bucket: b.bucket,
      label: bucketLabel(b.bucket, grain),
      rev: b.rev,
      expenses: b.expenses ?? null,
      margin: b.net_margin ?? null,
      hasCogs: cogs !== null,
    };
    for (const s of TREND_SERIES) p[s.key] = 0;
    p[COGS_SERIES] = cogs ?? 0;
    for (const [grp, v] of Object.entries(b.by_grp)) {
      if (grp === "rev" || reference.has(grp)) continue;
      if (!KNOWN.has(grp)) unknown.add(grp);
      const key = GROUP_TO_SERIES[grp] ?? (NAMED.has(grp) ? grp : "rest");
      p[key] = Math.round(((p[key] as number) + v) * 100) / 100;
    }
    return p;
  });
  return { points, unknown: [...unknown].sort() };
}
